import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { campaignsApi } from '@/lib/api/campaigns.api';

import type { CampaignQuery, CreateCampaignDto, UpdateCampaignDto } from '@repo/shared-types';

export function useCampaigns(query: Partial<CampaignQuery> = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['campaigns', query],
    queryFn: () => campaignsApi.getCampaigns(query),
    enabled: options?.enabled ?? true,
  });
}

export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', id],
    queryFn: () => campaignsApi.getCampaign(id!),
    enabled: !!id,
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCampaignDto) => campaignsApi.createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignDto }) =>
      campaignsApi.updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => campaignsApi.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}
